import React, { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { getSignedUrl } from "../services/Firebase";
import { TemplateThumb } from "./TemplateThumb";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

// Template info shape returned by GET /api/templates?type=carousel. With
// `looks: true` in the response, the list holds looks (templates the slide
// writer composes from); any look presents either post format.
interface TemplateInfo {
  id: string;
  name: string;
  supportedCategory: "listicle" | "quote";
  style_tags?: string[];
  example?: { videoPath?: string; imagePaths?: string[] };
  firstSlideDimensions?: { width: number; height: number };
  coverTimestamp?: number;
}

export interface TemplateSelectorProps {
  category?: "listicle" | "quote";
  /** IDs chosen for THIS generation (overrides BrandKit). Empty = fallback. */
  selectedTemplateIds: Set<string>;
  onSelectedLabelChange?: (label: string) => void;
  onChange: (ids: Set<string>) => void;
  /** Active BrandKit's allowedTemplateIds, if any. Used as fallback context. */
  brandKitAllowedTemplateIds?: string[];
  brandKitName?: string;
}

type Source = "custom" | "brandkit" | "all";

export function TemplateSelector({
  category,
  selectedTemplateIds,
  onSelectedLabelChange,
  onChange,
  brandKitAllowedTemplateIds,
  brandKitName,
}: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [urlCache, setUrlCache] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [looks, setLooks] = useState(false);

  // --- Fetch template catalog once on mount ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/templates?type=carousel`
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (cancelled) return;
        const fetched: TemplateInfo[] = Array.isArray(data.templates)
          ? data.templates
          : [];
        setLooks(data.looks === true);
        setTemplates(category && data.looks !== true ? fetched.filter(t => t.supportedCategory === category) : fetched);
      } catch (err) {
        console.error("TemplateSelector: failed to load templates", err);
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [category]);

  // Look mode: picks that are no longer looks (pruned or never seeded) drop out.
  useEffect(() => {
    if (!looks) return;
    const ids = new Set(templates.map((t) => t.id));
    const kept = Array.from(selectedTemplateIds).filter((id) => ids.has(id));
    if (kept.length !== selectedTemplateIds.size) onChange(new Set(kept));
  }, [looks, templates, selectedTemplateIds, onChange]);

  // Look mode: the Brand Kit's set counts only its looks.
  const kitTemplateIds = useMemo(
    () => looks
      ? brandKitAllowedTemplateIds?.filter((id) => templates.some((t) => t.id === id))
      : brandKitAllowedTemplateIds,
    [looks, templates, brandKitAllowedTemplateIds],
  );
  const noun = looks ? "look" : "style";

  useEffect(() => {
    onSelectedLabelChange?.(
      templates.filter((template) => selectedTemplateIds.has(template.id))
        .map((template) => template.name).join(", "),
    );
  }, [templates, selectedTemplateIds, onSelectedLabelChange]);

  // --- Preload first-slide Firebase URLs for fast thumbnail render ---
  useEffect(() => {
    if (templates.length === 0) return;
    let cancelled = false;
    (async () => {
      const paths: string[] = [];
      for (const t of templates) {
        if (t.example?.imagePaths?.[0]) paths.push(t.example.imagePaths[0]);
      }
      const missing = paths.filter((p) => !urlCache[p]);
      if (missing.length === 0) return;
      const results = await Promise.all(
        missing.map(async (path) => {
          try {
            const url = await getSignedUrl(path);
            return [path, url] as const;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      setUrlCache((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (r) next[r[0]] = r[1];
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally depend only on templates length — urlCache grows monotonically.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // --- Derive effective state ---
  const source: Source =
    selectedTemplateIds.size > 0
      ? "custom"
      : kitTemplateIds && kitTemplateIds.length > 0
        ? "brandkit"
        : "all";

  // Shuffled copy used ONLY when showing the full catalog (no override).
  // Stable per mount: fresh variety across visits, but doesn't re-order
  // mid-session (which would be disorienting).
  const shuffledAll = useMemo(() => {
    const copy = [...templates];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length]);

  const effectiveTemplates = useMemo(() => {
    if (source === "custom") {
      return templates.filter((t) => selectedTemplateIds.has(t.id));
    }
    if (source === "brandkit") {
      const ids = new Set(kitTemplateIds);
      return templates.filter((t) => ids.has(t.id));
    }
    return shuffledAll;
  }, [
    templates,
    shuffledAll,
    selectedTemplateIds,
    kitTemplateIds,
    source,
  ]);

  const effectiveCategories = useMemo(() => {
    const s = new Set<"listicle" | "quote">();
    for (const t of effectiveTemplates) s.add(t.supportedCategory);
    return s;
  }, [effectiveTemplates]);

  const onlyCategory =
    effectiveCategories.size === 1
      ? Array.from(effectiveCategories)[0]
      : null;

  const handleReset = useCallback(() => {
    onChange(new Set());
  }, [onChange]);

  // Inline showcase: a preview of variety, not a selection control. Most
  // users let AI auto-pick, so the strip's job is to reassure and teach.
  // Keep enough tiles to communicate variety; the scroll-edge fade + endcap
  // carry the "there's more here" signal.
  const PREVIEW_COUNT = 5;
  const previewTemplates = effectiveTemplates.slice(0, PREVIEW_COUNT);
  // Endcap count is scope-aware:
  //   - "all" mode → catalog overflow (templates - visible)
  //   - "brandkit" mode → kit overflow (allowed - visible), so the count
  //     stays within the user's current scope
  //   - "custom" mode → not used; endcap just says "Edit selection →"
  const endcapCount = (() => {
    if (source === "brandkit") {
      const allowed = kitTemplateIds?.length ?? 0;
      return Math.max(0, allowed - previewTemplates.length);
    }
    return Math.max(0, templates.length - previewTemplates.length);
  })();

  const subtitle = (() => {
    if (loading || templates.length === 0) return null;
    if (category && source !== "custom") {
      if (looks) return <>Choose a look for this post, or let Wavegen pick{source === "brandkit" ? " from your brand kit" : ""}.</>;
      return <>Choose a style for this {category === "listicle" ? "carousel" : "quote post"}, or let Wavegen pick{source === "brandkit" ? " from your brand kit" : ""}.</>;
    }
    if (source === "brandkit") {
      const count = kitTemplateIds?.length ?? 0;
      return (
        <>
          Drawing from your{" "}
          <span className="font-medium text-foreground">
            {brandKitName || "Brand Kit"}
          </span>{" "}
          ({count} {noun}{count === 1 ? "" : "s"}). AI picks per topic.
          {onlyCategory && !looks && (
            <>
              {" "}
              Only{" "}
              <span className="font-medium text-foreground capitalize">
                {onlyCategory}
              </span>{" "}
              posts will generate.
            </>
          )}
        </>
      );
    }
    if (source === "custom") {
      const count = selectedTemplateIds.size;
      return (
        <>
          <span className="font-medium text-foreground">
            {count} {noun}{count === 1 ? "" : "s"}
          </span>{" "}
          picked for this run. AI chooses per post.
          {onlyCategory && !looks && (
            <>
              {" "}
              Only{" "}
              <span className="font-medium text-foreground capitalize">
                {onlyCategory}
              </span>{" "}
              posts will generate.
            </>
          )}{" "}
          <button
            type="button"
            onClick={handleReset}
            className="text-foreground underline underline-offset-4 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
          >
            Clear
          </button>
        </>
      );
    }
    const roundedCount = Math.floor(templates.length / 10) * 10;
    if (looks) {
      return (
        <>
          Drawing from{" "}
          <span className="font-medium text-foreground">{roundedCount}+ looks</span>.
          AI picks per post.
        </>
      );
    }
    return (
      <>
        Drawing from{" "}
        <span className="font-medium text-foreground">{roundedCount}+ styles</span>{" "}
        across listicles, quotes, and more. AI picks per post.
      </>
    );
  })();

  // No catalog loaded — skip the section entirely rather than showing
  // an empty-state placeholder. Generation still works without templates.
  if (!loading && templates.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Section header — subtitle does the labeling; the strip's endcap
          tile carries the primary CTA (browse / edit) so the header never
          shifts layout between source modes. */}
      {subtitle && (
        <p className="text-sm text-muted-foreground max-w-prose">{subtitle}</p>
      )}

      {/* Showcase row */}
      <div className="relative">
        {loading ? (
          <div className="flex gap-3 overflow-hidden pb-1">
            {Array.from({ length: PREVIEW_COUNT + 1 }).map((_, i) => (
              <div
                key={i}
                className="flex-none w-[160px] rounded-md border border-border bg-muted animate-pulse"
                style={{ aspectRatio: "4/5" }}
              />
            ))}
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto scroll-smooth snap-x pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {previewTemplates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setModalOpen(true)}
                className="group flex-none snap-start w-[160px] rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label={`${t.name} — browse ${noun}s`}
              >
                <div className="relative overflow-hidden rounded-md border border-border group-hover:shadow-sm transition-shadow">
                  <TemplateThumb
                    example={t.example}
                    aspect={t.firstSlideDimensions}
                    templateId={t.id}
                    currentIndex={0}
                    urlCache={urlCache}
                  />
                </div>
              </button>
            ))}
            {(source === "custom" || endcapCount > 0) && (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="group flex-none snap-start w-[160px] rounded-md border border-border bg-muted/40 hover:bg-muted transition-colors flex flex-col items-center justify-center gap-1 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                style={{ aspectRatio: "4/5" }}
                aria-label={
                  source === "custom"
                    ? "Edit selection"
                    : `Browse ${endcapCount} more ${noun}s`
                }
              >
                {source === "custom" ? (
                  <>
                    <span className="text-base font-semibold text-foreground leading-none">
                      Edit
                    </span>
                    <span className="text-xs text-muted-foreground tracking-wide">
                      selection&nbsp;→
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl font-semibold text-foreground leading-none">
                      +{endcapCount}
                    </span>
                    <span className="text-xs text-muted-foreground tracking-wide">
                      more {noun}s&nbsp;→
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        )}
        {!loading && templates.length > 0 && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent"
          />
        )}
      </div>

      <TemplateSelectorModal
        category={category}
        looks={looks}
        open={modalOpen}
        onOpenChange={setModalOpen}
        templates={templates}
        urlCache={urlCache}
        setUrlCache={setUrlCache}
        initialSelectedIds={
          source === "brandkit"
            ? // Seed the draft with the kit's templates so opening the
              // modal reads as "edit your kit's set for this run." Filter
              // to valid IDs only — orphan refs would otherwise show as
              // ghost selections that don't render in the grid.
              new Set(
                (kitTemplateIds ?? []).filter((id) =>
                  templates.some((t) => t.id === id)
                )
              )
            : selectedTemplateIds
        }
        brandKitAllowedTemplateIds={kitTemplateIds}
        brandKitName={brandKitName}
        onApply={(ids) => {
          onChange(ids);
          setModalOpen(false);
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        active
          ? "bg-foreground text-background"
          : "bg-transparent text-foreground hover:bg-muted"
      )}
    >
      {children}
    </button>
  );
}

interface ModalProps {
  category?: "listicle" | "quote";
  /** One gallery of looks: no format filters or format copy. */
  looks?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templates: TemplateInfo[];
  urlCache: Record<string, string>;
  setUrlCache: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  initialSelectedIds: Set<string>;
  brandKitAllowedTemplateIds?: string[];
  brandKitName?: string;
  onApply: (ids: Set<string>) => void;
}

function TemplateSelectorModal({
  category,
  looks = false,
  open,
  onOpenChange,
  templates,
  urlCache,
  setUrlCache,
  initialSelectedIds,
  brandKitAllowedTemplateIds,
  brandKitName,
  onApply,
}: ModalProps) {
  const [draft, setDraft] = useState<Set<string>>(
    new Set(initialSelectedIds)
  );
  const [activeFormat, setActiveFormat] = useState<
    "all" | "listicle" | "quote"
  >("all");
  const [previewIndex, setPreviewIndex] = useState<Record<string, number>>({});

  // Reset draft + filters when modal opens with a fresh initial state
  useEffect(() => {
    if (open) {
      setDraft(new Set(initialSelectedIds));
      setActiveFormat("all");
    }
  }, [open, initialSelectedIds]);

  // Preload ALL carousel slide URLs when modal is open (for hover scrubbing)
  useEffect(() => {
    if (!open || templates.length === 0) return;
    let cancelled = false;
    (async () => {
      const paths: string[] = [];
      for (const t of templates) {
        if (t.example?.imagePaths) paths.push(...t.example.imagePaths);
      }
      const missing = paths.filter((p) => !urlCache[p]);
      if (missing.length === 0) return;
      const results = await Promise.all(
        missing.map(async (path) => {
          try {
            const url = await getSignedUrl(path);
            return [path, url] as const;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      setUrlCache((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (r) next[r[0]] = r[1];
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templates]);

  // Counts per format — used as quiet numerals on the segment pills.
  const formatCounts = useMemo(() => {
    let listicle = 0;
    let quote = 0;
    for (const t of templates) {
      if (t.supportedCategory === "listicle") listicle++;
      else if (t.supportedCategory === "quote") quote++;
    }
    return { listicle, quote, all: templates.length };
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    if (activeFormat === "all") return templates;
    return templates.filter((t) => t.supportedCategory === activeFormat);
  }, [templates, activeFormat]);

  const toggle = useCallback((id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setActiveFormat("all");
  }, []);

  const selectAllVisible = useCallback(() => {
    setDraft((prev) => {
      const next = new Set(prev);
      for (const t of filteredTemplates) next.add(t.id);
      return next;
    });
  }, [filteredTemplates]);

  const handlePrevSlide = useCallback(
    (e: React.MouseEvent, templateId: string, totalSlides: number) => {
      e.stopPropagation();
      e.preventDefault();
      setPreviewIndex((prev) => {
        const current = prev[templateId] ?? 0;
        return {
          ...prev,
          [templateId]: current === 0 ? totalSlides - 1 : current - 1,
        };
      });
    },
    []
  );

  const handleNextSlide = useCallback(
    (e: React.MouseEvent, templateId: string, totalSlides: number) => {
      e.stopPropagation();
      e.preventDefault();
      setPreviewIndex((prev) => {
        const current = prev[templateId] ?? 0;
        return {
          ...prev,
          [templateId]: current === totalSlides - 1 ? 0 : current + 1,
        };
      });
    },
    []
  );

  const draftCategories = useMemo(() => {
    const s = new Set<"listicle" | "quote">();
    for (const id of draft) {
      const t = templates.find((x) => x.id === id);
      if (t) s.add(t.supportedCategory);
    }
    return s;
  }, [draft, templates]);

  const singleCategoryLock =
    draft.size > 0 && draftCategories.size === 1
      ? Array.from(draftCategories)[0]
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[96vw] max-w-[1100px] sm:max-w-[1100px] h-[92vh] max-h-[92vh] overflow-hidden flex flex-col sm:rounded-xl"
      >
        <DialogHeader>
          <DialogTitle>{looks ? "Pick looks for this run" : "Pick styles for this run"}</DialogTitle>
          <DialogDescription>
            {category ? (
              <>Used for this post and saved for this source. Your brand kit stays intact.</>
            ) : brandKitAllowedTemplateIds &&
            brandKitAllowedTemplateIds.length > 0 ? (
              <>
                Applies to this run only. Your{" "}
                {brandKitName ? (
                  <span className="font-medium">{brandKitName}</span>
                ) : (
                  "Brand Kit"
                )}{" "}
                stays intact.
              </>
            ) : (
              <>Applies to this run only. Your defaults don't change.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Filters on the left, bulk selection actions on the right —
            both scope what shows / gets selected in the grid below.
            On mobile, stack into two rows (pills above, actions below)
            so toggling Clear all visibility never reflows the pills. */}
        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          {looks ? null : category ? <p className="text-sm font-medium">{category === "listicle" ? "Carousel styles" : "Quote styles"}</p> : <div className="flex items-center gap-1 flex-wrap">
            <FilterPill
              active={activeFormat === "all"}
              onClick={() => setActiveFormat("all")}
            >
              All formats
            </FilterPill>
            <FilterPill
              active={activeFormat === "listicle"}
              onClick={() => setActiveFormat("listicle")}
            >
              Listicles
              <span
                className={cn(
                  "ml-1.5 text-xs font-normal",
                  activeFormat === "listicle"
                    ? "text-background/60"
                    : "text-muted-foreground"
                )}
              >
                {formatCounts.listicle}
              </span>
            </FilterPill>
            <FilterPill
              active={activeFormat === "quote"}
              onClick={() => setActiveFormat("quote")}
            >
              Quotes
              <span
                className={cn(
                  "ml-1.5 text-xs font-normal",
                  activeFormat === "quote"
                    ? "text-background/60"
                    : "text-muted-foreground"
                )}
              >
                {formatCounts.quote}
              </span>
            </FilterPill>
          </div>}
          {/* DOM order is [Select all][Clear all] so on mobile (left-anchored)
              Select all stays put when Clear all appears to its right. On
              desktop we reverse visually so Select all sits at the right edge
              with Clear all to its left — same "no reflow" property both ways. */}
          <div className="flex items-center gap-2 self-start sm:self-auto sm:ml-auto sm:flex-row-reverse">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectAllVisible}
            >
              Select all
            </Button>
            {draft.size > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDraft(new Set())}
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-3 pb-4">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No styles match these filters.{" "}
              <button
                type="button"
                onClick={resetFilters}
                className="text-foreground underline underline-offset-4 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredTemplates.map((t) => {
                const isSelected = draft.has(t.id);
                const paths = t.example?.imagePaths || [];
                const slides = paths.length;
                const curIdx = previewIndex[t.id] ?? 0;
                return (
                  <div
                    key={t.id}
                    className={cn(
                      "relative group rounded-md border-2 overflow-hidden bg-card transition-shadow duration-200 hover:shadow-sm",
                      isSelected ? "border-foreground" : "border-border"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(t.id)}
                      className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                      aria-pressed={isSelected}
                      aria-label={`${t.name}${isSelected ? " — selected" : ""}`}
                    >
                      <TemplateThumb
                        example={t.example}
                        aspect={t.firstSlideDimensions}
                        templateId={t.id}
                        currentIndex={curIdx}
                        urlCache={urlCache}
                      />
                    </button>

                    {/* Selected marker — editorial corner tag */}
                    {isSelected && (
                      <span className="absolute top-2 right-2 bg-foreground text-background text-[10px] font-medium tracking-wider uppercase px-1.5 py-0.5 rounded-sm pointer-events-none">
                        Selected
                      </span>
                    )}

                    {/* Carousel arrows + dots — siblings to the toggle button
                        so we don't nest interactive controls inside a button. */}
                    {slides > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => handlePrevSlide(e, t.id, slides)}
                          className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-foreground/70 hover:bg-foreground/85 flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          aria-label="Previous slide"
                        >
                          <svg
                            className="w-3 h-3 text-background"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleNextSlide(e, t.id, slides)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-foreground/70 hover:bg-foreground/85 flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          aria-label="Next slide"
                        >
                          <svg
                            className="w-3 h-3 text-background"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                        {/* Slide dots */}
                        <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1 z-10 pointer-events-none">
                          {paths.map((_, i) => (
                            <span
                              key={i}
                              className={cn(
                                "h-1.5 rounded-full transition-all [filter:drop-shadow(0_0_1px_rgba(0,0,0,0.4))]",
                                curIdx === i
                                  ? "bg-background w-2.5"
                                  : "bg-background/60 w-1.5"
                              )}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t pt-3">
          <p className="text-sm text-left flex-1 text-muted-foreground">
            {draft.size === 0 ? (
              brandKitAllowedTemplateIds &&
              brandKitAllowedTemplateIds.length > 0 ? (
                <>
                  <span>AI will draw from your </span>
                  <span className="font-medium text-foreground">
                    {brandKitName || "Brand Kit"}
                  </span>
                  <span>.</span>
                </>
              ) : (
                <>
                  <span>AI will draw from all </span>
                  <span className="font-medium text-foreground">
                    {`${templates.length} ${looks ? "looks" : "styles"}`}
                  </span>
                  <span>.</span>
                </>
              )
            ) : (
              <>
                <span className="font-medium text-foreground">
                  {`${draft.size} ${looks ? "look" : "style"}${draft.size === 1 ? "" : "s"}`}
                </span>
                <span> selected</span>
                {singleCategoryLock && !looks ? (
                  <>
                    <span>
                      {` · all ${
                        singleCategoryLock === "listicle"
                          ? "listicles"
                          : "quotes"
                      }. `}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveFormat(
                          singleCategoryLock === "listicle"
                            ? "quote"
                            : "listicle"
                        )
                      }
                      className="text-foreground underline underline-offset-4 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                    >
                      <span>
                        {`Mix in ${
                          singleCategoryLock === "listicle"
                            ? "quotes"
                            : "listicles"
                        } →`}
                      </span>
                    </button>
                  </>
                ) : (
                  <span>.</span>
                )}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => onApply(draft)}
            >
              Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
